import { Link, Route, Routes, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EMAIL_TEMPLATES, type EmailTemplate } from "@demore/shared/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function NdaTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["settings-nda"],
    queryFn: () => api.get<{ items: Array<{ id: string; version: number; body: string; active: boolean }> }>("/api/admin/settings/nda"),
  });
  const [body, setBody] = useState("");
  const save = useMutation({
    mutationFn: () => api.post("/api/admin/settings/nda", { body, activate: true }),
    onSuccess: () => { toast.success("New NDA version saved & activated"); qc.invalidateQueries({ queryKey: ["settings-nda"] }); setBody(""); },
  });
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>New NDA version</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} placeholder="NDA text…" />
          <Button onClick={() => save.mutate()} disabled={!body || save.isPending}>{save.isPending ? "Saving…" : "Save & activate"}</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>History</CardTitle></CardHeader>
        <CardContent>
          <ul className="text-sm space-y-2">
            {data?.items.map((t) => (
              <li key={t.id} className="border border-admin-rule rounded-md p-3">
                <div className="flex justify-between mb-1">
                  <span>v{t.version}</span>
                  {t.active && <span className="text-gold text-xs">ACTIVE</span>}
                </div>
                <pre className="text-xs whitespace-pre-wrap text-muted-foreground line-clamp-4">{t.body}</pre>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

type EmailTemplateRow = { id: string; key: EmailTemplate; subject: string; html: string; text: string | null };

function EmailTemplatesTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["settings-email-templates"],
    queryFn: () => api.get<{ items: EmailTemplateRow[] }>("/api/admin/settings/email-templates"),
  });
  const [activeKey, setActiveKey] = useState<EmailTemplate>(EMAIL_TEMPLATES[0]);
  const tpl = data?.items.find((t) => t.key === activeKey);

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  useEffect(() => {
    setSubject(tpl?.subject ?? "");
    setHtml(tpl?.html ?? "");
    setText(tpl?.text ?? "");
  }, [tpl?.id, tpl?.key]);

  const save = useMutation({
    mutationFn: () => api.put("/api/admin/settings/email-templates", {
      key: activeKey, subject, html, text: text || undefined,
    }),
    onSuccess: () => { toast.success("Template saved"); qc.invalidateQueries({ queryKey: ["settings-email-templates"] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Email template</CardTitle>
            <Select
              value={activeKey}
              onChange={(e) => setActiveKey(e.target.value as EmailTemplate)}
              className="w-72"
            >
              {EMAIL_TEMPLATES.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>HTML body</Label>
            <Textarea rows={14} value={html} onChange={(e) => setHtml(e.target.value)} className="font-mono text-xs" />
          </div>
          <div className="space-y-1.5">
            <Label>Plain-text body (optional)</Label>
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs" />
          </div>
          <div className="text-xs text-muted-foreground">
            Tokens: <code>{"{{recipientName}}, {{senderFirstName}}, {{viewerUrl}}, {{playlistName}}, {{expiryDate}}, {{eventDescription}}, {{packUrl}}, {{forwarderName}}, {{inviterName}}, {{inviteUrl}}, {{resetUrl}}, {{name}}, {{count}}, {{reviewUrl}}"}</code>
          </div>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save template"}</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function PromotedTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["settings-promoted"],
    queryFn: () => api.get<{ items: Array<{ id: string; videoId: string; active: boolean; title: string }> }>("/api/admin/settings/promoted"),
  });
  const remove = useMutation({
    mutationFn: (videoId: string) => api.delete(`/api/admin/videos/${videoId}/promote`),
    onSuccess: () => {
      toast.success("Removed from promoted");
      qc.invalidateQueries({ queryKey: ["settings-promoted"] });
      // Bug 3: also refresh the video-detail promoted state + builder tab.
      qc.invalidateQueries({ queryKey: ["promoted-list"] });
      qc.invalidateQueries({ queryKey: ["library-tabbed"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });
  const items = (data?.items ?? []).filter((i) => i.active);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Promoted videos ({items.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Videos surfaced first in the playlist builder. Promote from any video detail page.
        </p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing promoted yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {items.map((p) => (
              <li key={p.id} className="flex items-center justify-between border-b border-admin-rule py-2">
                <Link to={`/admin/library/${p.videoId}`} className="hover:text-gold">{p.title}</Link>
                <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => remove.mutate(p.videoId)}>
                  Unpromote
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function SettingsPage() {
  return (
    <div className="space-y-4 max-w-5xl">
      <h1 className="font-head text-2xl font-semibold">Settings</h1>
      <div className="flex gap-1 border-b border-admin-rule">
        {["nda", "email-templates", "promoted"].map((tab) => (
          <NavLink
            key={tab}
            to={`/admin/settings/${tab}`}
            className={({ isActive }) =>
              cn("px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                isActive ? "border-gold text-gold" : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {tab.replace(/-/g, " ")}
          </NavLink>
        ))}
      </div>
      <Routes>
        <Route index element={<NdaTab />} />
        <Route path="nda" element={<NdaTab />} />
        <Route path="email-templates" element={<EmailTemplatesTab />} />
        <Route path="promoted" element={<PromotedTab />} />
      </Routes>
    </div>
  );
}
